import { useCallback, useMemo, useState } from 'react';
import { AiMatchedProduct } from 'types/schema';
import { AnalyzedItem } from '../types';

export function useAnalyzeResult() {
  const [products, setProducts] = useState<AnalyzedItem[]>([]);
  const [quantities, setQuantities] = useState<Record<string, number>>({});

  const reset = useCallback((next: AnalyzedItem[]) => {
    setProducts(next);
    setQuantities(
      next.reduce<Record<string, number>>((acc, p) => {
        acc[p.id] = p.defaultQuantity;
        return acc;
      }, {}),
    );
  }, []);

  const toggleRow = useCallback((id: string) => {
    setProducts((prev) => prev.map((p) => (p.id === id ? { ...p, selected: !p.selected } : p)));
  }, []);

  const toggleAll = useCallback(() => {
    setProducts((prev) => {
      const selectable = prev.filter((p) => !!p.matchedProduct);
      const allSelected = selectable.length > 0 && selectable.every((p) => p.selected);
      return prev.map((p) => (p.matchedProduct ? { ...p, selected: !allSelected } : p));
    });
  }, []);

  const selectAllHigh = useCallback(() => {
    setProducts((prev) =>
      prev.map((p) =>
        p.confidence?.toLowerCase() === 'high' && !!p.matchedProduct ? { ...p, selected: true } : p,
      ),
    );
  }, []);

  const setQuantity = useCallback((id: string, value: number) => {
    setQuantities((prev) => ({ ...prev, [id]: value }));
  }, []);

  const swapProduct = useCallback((id: string, alternative: AiMatchedProduct) => {
    setProducts((prev) =>
      prev.map((p) =>
        p.id === id
          ? { ...p, matchedProduct: alternative, selected: p.matchedProduct ? p.selected : true }
          : p,
      ),
    );
  }, []);

  const derived = useMemo(() => {
    const highRows = products.filter((p) => p.confidence?.toLowerCase() === 'high' && !!p.matchedProduct);
    const allHighSelected = highRows.length > 0 && highRows.every((p) => p.selected);
    const selectedHighCount = highRows.filter((p) => p.selected).length;
    const selected = products.filter((p) => p.selected);
    const selectedCount = selected.length;
    const totalAmount = selected.reduce(
      (sum, p) => sum + (p.matchedProduct?.price ?? 0) * (quantities[p.id] ?? p.defaultQuantity),
      0,
    );
    const found = products.filter((p) => !!p.matchedProduct).length;
    const hasHigh = highRows.length > 0;
    const selectable = products.filter((p) => !!p.matchedProduct);
    const allSelected = selectable.length > 0 && selectable.every((p) => p.selected);
    const someSelected = !allSelected && selectable.some((p) => p.selected);
    return { allHighSelected: allHighSelected, hasHigh: hasHigh, selectedHighCount: selectedHighCount, selectedCount: selectedCount, totalAmount: totalAmount, found: found, allSelected: allSelected, someSelected: someSelected };
  }, [products, quantities]);

  return { products: products, quantities: quantities, toggleRow: toggleRow, toggleAll: toggleAll, selectAllHigh: selectAllHigh, setQuantity: setQuantity, swapProduct: swapProduct, reset: reset, ...derived };
}
